import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { trainingApi } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import SignaturePad from '../SignaturePad';
import { Archive, CheckCheck, FileDown, Loader2, PenLine, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

function SigBlock({ sig, label, onSign, testId, disabled }) {
  if (sig?.image) {
    return (
      <div className="text-center" data-testid={`${testId}-done`}>
        <img src={sig.image} alt={label} className="h-10 mx-auto" />
        <p className="text-[10px] text-slate-500">{label}: <b>{sig.name}</b><br />{(sig.at || '').slice(0, 16).replace('T', ' ')}</p>
      </div>
    );
  }
  if (disabled) return <span className="text-xs text-slate-400">{label}: non firmata</span>;
  return (
    <Button variant="outline" size="sm" className="h-8 gap-1.5 rounded-none" onClick={onSign} data-testid={testId}>
      <PenLine className="w-3.5 h-3.5" /> {label}
    </Button>
  );
}

export default function EvaluationDetail({ evaluationId, meta, onClose }) {
  const { user } = useAuth();
  const [ev, setEv] = useState(null);
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState('');
  const [level, setLevel] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [signCtx, setSignCtx] = useState(null); // {signer, label}
  const [showPdf, setShowPdf] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = useCallback(() => {
    trainingApi.getEvaluation(evaluationId)
      .then(r => {
        setEv(r.data);
        setScores(r.data.scores || {});
        setNotes(r.data.notes || '');
        setLevel(r.data.level || '');
        setDirty(false);
      })
      .catch(e => { toast.error(e.response?.data?.detail || 'Errore'); onClose(); });
  }, [evaluationId, onClose]);

  useEffect(() => { load(); }, [load]);

  const tpl = useMemo(() => ev?.template || {}, [ev]);
  const scaleMin = tpl.scale_min || 1;
  const scaleMax = tpl.scale_max || 5;
  const isFinal = ev?.status === 'final';
  const canEdit = meta.can_evaluate && !isFinal;

  const computed = useMemo(() => {
    let total = 0, maxTotal = 0, scored = 0;
    const secTotals = {};
    for (const sec of tpl.sections || []) {
      let st = 0;
      for (const it of sec.items || []) {
        maxTotal += scaleMax;
        const v = scores[it.item_id];
        if (Number.isInteger(v)) { st += v; scored += 1; }
      }
      secTotals[sec.section_id] = st;
      total += st;
    }
    return { total, maxTotal, scored, secTotals };
  }, [scores, tpl, scaleMax]);

  const setScore = (itemId, v) => {
    if (!canEdit) return;
    setScores(s => ({ ...s, [itemId]: v }));
    setDirty(true);
  };

  const persistNow = useCallback(async (silent = true) => {
    setSaving(true);
    try {
      await trainingApi.updateEvaluation(evaluationId, { scores, notes: notes || null, level: level || null });
      setDirty(false);
      if (!silent) toast.success('Scheda salvata');
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio fallito');
      return false;
    } finally {
      setSaving(false);
    }
  }, [evaluationId, scores, notes, level]);

  // Autosave: la bozza si salva da sola 1.2s dopo l'ultima modifica.
  useEffect(() => {
    if (!dirty || !canEdit) return undefined;
    const t = setTimeout(() => { persistNow(true); }, 1200);
    return () => clearTimeout(t);
  }, [dirty, canEdit, persistNow]);

  const save = () => persistNow(false);

  const finalize = async () => {
    if (dirty && !(await persistNow(true))) return;
    if (!window.confirm('Rendere la scheda definitiva? Non sarà più modificabile.')) return;
    try {
      await trainingApi.finalizeEvaluation(evaluationId);
      toast.success('Scheda finalizzata');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Finalizzazione fallita'); }
  };

  const openSign = async (ctx) => {
    if (canEdit && dirty && !(await persistNow(true))) return;
    setSignCtx(ctx);
  };

  const doSign = async (b64) => {
    try {
      await trainingApi.signEvaluation(evaluationId, { signer: signCtx.signer, signature_b64: b64 });
      toast.success('Firma registrata');
      setSignCtx(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Firma fallita'); }
  };

  const openPdf = async (withReport) => {
    setPdfBusy(true);
    // Apri la scheda SUBITO (sincrono col click): se aperta dopo l'await il
    // popup-blocker la blocca quando la generazione AI richiede più secondi.
    const win = window.open('', '_blank');
    try {
      const r = await trainingApi.evaluationPdf(evaluationId, withReport);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      if (win && !win.closed) win.location = url; else window.open(url, '_blank');
      setShowPdf(false);
    } catch (e) {
      if (win && !win.closed) win.close();
      toast.error(withReport ? 'Generazione report AI non riuscita, riprova' : 'Errore generazione PDF');
    } finally {
      setPdfBusy(false);
    }
  };

  const doArchive = async () => {
    if (!window.confirm(`Salvare il PDF nel profilo di ${ev.staff_name}, cartella Formazione dei Documenti?`)) return;
    setSaving(true);
    try {
      await trainingApi.archiveEvaluation(evaluationId);
      toast.success('PDF archiviato nel profilo staff → cartella Formazione');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Archiviazione fallita'); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!window.confirm('Eliminare definitivamente questa scheda?')) return;
    try {
      await trainingApi.deleteEvaluation(evaluationId);
      toast.success('Scheda eliminata');
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eliminazione fallita'); }
  };

  if (!ev) return null;

  return (
    <>
      <Dialog open onOpenChange={v => { if (!v && !signCtx) onClose(); }}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="evaluation-detail"
                       onInteractOutside={e => { if (signCtx) e.preventDefault(); }}
                       onEscapeKeyDown={e => { if (signCtx) e.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              Scheda di valutazione — {ev.staff_name}
              <Badge variant="outline" className={isFinal
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>
                {isFinal ? 'Definitiva' : 'Bozza'}
              </Badge>
              {ev.filed_doc_id && <Badge variant="outline" className="bg-slate-100">Archiviata</Badge>}
            </DialogTitle>
            <DialogDescription>
              {ev.store_name || '—'} · Valutatore: {ev.evaluator_name || '—'} · Data: {ev.date || '—'}
              {ev.period ? ` · Periodo: ${ev.period}` : ''}
            </DialogDescription>
          </DialogHeader>

          {/* Riepilogo punteggio */}
          <div className="grid grid-cols-3 gap-2 bg-slate-900 text-white p-3" data-testid="evaluation-summary">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400">Punteggio</p>
              <p className="text-xl font-bold tabular-nums">{computed.total}<span className="text-sm text-slate-400"> / {computed.maxTotal}</span></p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400">Voci valutate</p>
              <p className="text-xl font-bold tabular-nums">{computed.scored}<span className="text-sm text-slate-400"> / {(tpl.sections || []).reduce((a, s) => a + s.items.length, 0)}</span></p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400">Livello (mansione)</p>
              {canEdit && (meta.evaluation_levels || []).length > 0 ? (
                <Select value={level} onValueChange={v => { setLevel(v); setDirty(true); }}>
                  <SelectTrigger className="h-8 mt-1 rounded-none bg-white/10 border-slate-600 text-white text-sm"
                                 data-testid="evaluation-level-select">
                    <SelectValue placeholder="Seleziona livello" />
                  </SelectTrigger>
                  <SelectContent>
                    {(meta.evaluation_levels || []).map(lv => (
                      <SelectItem key={lv} value={lv}>{lv}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-xl font-bold" data-testid="evaluation-level">{level || '—'}</p>
              )}
            </div>
          </div>

          {/* Sezioni */}
          <div className="space-y-4">
            {(tpl.sections || []).map(sec => (
              <div key={sec.section_id} className="border border-slate-200" data-testid={`eval-section-${sec.section_id}`}>
                <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
                  <h3 className="font-heading text-xs font-bold uppercase tracking-wider text-slate-700">{sec.title}</h3>
                  <span className="text-xs text-slate-500 tabular-nums">{computed.secTotals[sec.section_id] || 0} / {sec.items.length * scaleMax}</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {sec.items.map(it => (
                    <div key={it.item_id} className="flex flex-col items-start gap-1.5 px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-2 sm:py-1.5">
                      <span className="text-sm text-slate-700">{it.label}</span>
                      <div className="flex gap-1.5 sm:gap-1">
                        {Array.from({ length: scaleMax - scaleMin + 1 }, (_, i) => scaleMin + i).map(v => (
                          <button key={v} type="button" onClick={() => setScore(it.item_id, v)}
                                  disabled={!canEdit}
                                  className={`w-9 h-9 text-sm sm:w-7 sm:h-7 sm:text-xs border transition-colors tabular-nums ${scores[it.item_id] === v
                                    ? 'bg-slate-900 text-white border-slate-900'
                                    : 'bg-white text-slate-500 border-slate-300 hover:border-slate-500 disabled:opacity-40'}`}
                                  data-testid={`eval-score-${it.item_id}-${v}`}>
                            {v}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Note */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Note</label>
            <Textarea value={notes} onChange={e => { setNotes(e.target.value); setDirty(true); }}
                      disabled={!canEdit} rows={2} className="rounded-none mt-1 text-sm" data-testid="evaluation-notes" />
          </div>

          {/* Firme */}
          <div className="flex gap-4 flex-wrap items-center border-t border-slate-200 pt-3">
            <SigBlock sig={ev.signatures?.evaluator} label="Firma valutatore" testId="eval-sign-evaluator"
                      disabled={!meta.can_evaluate}
                      onSign={() => openSign({ signer: 'evaluator', label: 'Firma valutatore' })} />
            <SigBlock sig={ev.signatures?.staff} label="Firma dipendente" testId="eval-sign-staff"
                      onSign={() => openSign({ signer: 'staff', label: `Firma dipendente — ${ev.staff_name}` })} />
          </div>

          <DialogFooter className="flex-wrap gap-2 items-center">
            {user?.role === 'operations_manager' && (
              <Button variant="outline" size="sm" className={`h-8 text-red-600 border-red-200 ${canEdit ? '' : 'mr-auto'}`} onClick={doDelete} data-testid="evaluation-delete-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Elimina
              </Button>
            )}
            {canEdit && (
              <span className="text-[11px] text-slate-400 mr-auto" data-testid="evaluation-autosave-status">
                {saving ? 'Salvataggio…' : dirty ? 'Modifiche in attesa…' : 'Salvato automaticamente ✓'}
              </span>
            )}
            {canEdit && (
              <Button variant={dirty ? 'default' : 'outline'} size="sm" className="h-8 gap-1.5" onClick={save} disabled={saving} data-testid="evaluation-save-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Salva
              </Button>
            )}
            {canEdit && (
              <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={finalize} data-testid="evaluation-finalize-btn">
                <CheckCheck className="w-3.5 h-3.5" /> Finalizza
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => setShowPdf(true)} data-testid="evaluation-pdf-btn">
              <FileDown className="w-3.5 h-3.5" /> PDF
            </Button>
            {meta.can_evaluate && (
              <Button size="sm" className="h-8 gap-1.5" onClick={doArchive} disabled={saving} data-testid="evaluation-archive-btn">
                <Archive className="w-3.5 h-3.5" /> Archivia nei documenti
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {signCtx && (
        <SignaturePad title={signCtx.label} subtitle={`${ev.staff_name} — scheda di valutazione`}
                      onSubmit={doSign} onCancel={() => setSignCtx(null)} />
      )}

      {showPdf && (
        <Dialog open onOpenChange={v => { if (!v && !pdfBusy) setShowPdf(false); }}>
          <DialogContent className="max-w-md" data-testid="pdf-options-dialog">
            <DialogHeader>
              <DialogTitle>Scarica PDF</DialogTitle>
              <DialogDescription>
                Vuoi includere nel PDF un report che analizza la scheda (sintesi del profilo, punti di forza,
                aree di miglioramento e suggerimenti formativi generati dall'AI)?
              </DialogDescription>
            </DialogHeader>
            {pdfBusy && (
              <p className="text-sm text-slate-500 flex items-center gap-2" data-testid="pdf-generating">
                <Loader2 className="w-4 h-4 animate-spin" /> Generazione in corso… può richiedere qualche secondo.
              </p>
            )}
            <DialogFooter className="flex-wrap gap-2">
              <Button variant="outline" onClick={() => openPdf(false)} disabled={pdfBusy} data-testid="pdf-plain-btn">
                Solo scheda
              </Button>
              <Button onClick={() => openPdf(true)} disabled={pdfBusy} data-testid="pdf-with-report-btn">
                {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null} Scheda + report di analisi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
